/**
 * Brand colours for the values these screens repeat constantly.
 *
 * All drawn from the palette in `tailwind.config.js` — primary (green),
 * secondary (coral), creams, action and neutral. Nothing invented: a one-off
 * hex here is a colour nobody else can reuse and that no design change reaches.
 *
 * The rule behind the choices: colour carries meaning or it is noise. A
 * vertical is a category, so the three get distinct hues. A status is a
 * position in a funnel, so it runs cool to warm. Anything needing attention
 * borrows the alert and danger colours already used elsewhere in the admin.
 */
const CHIP_SHAPE = 'inline-flex items-center rounded-full px-2.5 py-0.5';

export const CHIP = `${CHIP_SHAPE} text-xs font-gilroy-medium`;

/*
  Three weights, because a row of five equal chips is a wall, not a summary.

  A lead's status is the thing an agent is scanning for; the reason it holds
  that status qualifies it; the SLA and the attempts left are context you read
  once you have stopped on the row. Giving all three the same filled pill made
  "Breached" shout as loudly as "New" and left nothing to anchor on.

  Order on the row follows the same ranking — status, reason, SLA, attempts —
  so weight and position say the same thing.
*/

/*
  Each weight is a COMPLETE chip, not a modifier stacked on CHIP.

  The first version set `CHIP_PRIMARY = 'font-gilroy-bold'` and applied it
  as `${CHIP} ${CHIP_PRIMARY}`, which left `text-xs` from CHIP fighting the
  size the weight wanted. Two conflicting Tailwind utilities on one element
  are resolved by stylesheet order, not by the order they appear in the
  class string — so the status chip silently kept the small size and ended
  up *smaller* than the SLA text beside it, which is the opposite of the
  point. Composing whole chips removes the conflict.
*/

/** Status. Largest and bold: the one chip the eye should land on first. */
export const CHIP_PRIMARY = `${CHIP_SHAPE} text-sm font-gilroy-bold`;

/** Sub-status — a resolution type or a pending reason. A step down. */
export const CHIP_SECONDARY = `${CHIP_SHAPE} text-xs font-gilroy-medium`;

/**
 * SLA and attempts. No fill at all — colour on the text and the dot only.
 *
 * A breach still reads red, which it must, but as a note rather than a second
 * badge competing with the status beside it.
 */
export const CHIP_QUIET =
  'inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 text-[11px] font-gilroy-regular';

/**
 * Three categories, three hues. Not a ranking — no colour is "better".
 *
 * None of them is coral. Red is reserved for a lost outcome, and a furnishing
 * lead is not a bad thing: a vertical wearing the loss colour makes a healthy
 * queue look alarming.
 */
export const VERTICAL_STYLE: Record<string, string> = {
  furnishing: 'bg-blue text-neutral-500',
  acquisition: 'bg-primary-50 text-primary-700',
  reservation: 'bg-creams-500 text-creams-900'
};

/**
 * Cool while the lead is live, green when it lands, warm when it stalls.
 * `duplicate` is deliberately flat — it is bookkeeping, not an outcome.
 */
export const STATUS_STYLE: Record<string, string> = {
  new: 'bg-blue text-neutral-500',
  pending: 'bg-creams-500 text-creams-900',
  resolved: 'bg-primary-100 text-primary-800',
  // Warm, but not coral — reopening a lead is activity, not a loss.
  reopened: 'bg-creams-600 text-creams-900',
  not_reachable: 'bg-neutral-50 text-neutral-400',
  duplicate: 'bg-neutral-50 text-neutral-300'
};

/**
 * Why a lead is on hold — a qualifier of the `pending` status, not a signal
 * of its own.
 *
 * Deliberately colourless. The rule at the top of this file is that colour
 * carries meaning or it is noise, and a pending reason does not rank: nobody
 * should read "waiting on the client" as better or worse than "budget not
 * approved". A hue here would also fight the cream `pending` chip it sits
 * beside and the cream attempts chip two along, turning the Labels column
 * into four competing warm blobs.
 *
 * Neutral keeps it legible as a footnote to the status it qualifies.
 */
export const PENDING_REASON_CHIP = 'bg-neutral-50 text-neutral-400';

/** Seniority in the reporting tree, darkest at the top. */
export const ROLE_STYLE: Record<string, string> = {
  manager: 'bg-primary-500 text-neutral-0',
  lead: 'bg-primary-200 text-primary-900',
  senior: 'bg-primary-50 text-primary-700',
  agent: 'bg-creams-400 text-creams-900'
};

export const styleFor = (map: Record<string, string>, key?: string): string =>
  (key && map[key]) || 'bg-neutral-50 text-neutral-400';

/**
 * Colour for how a lead ended.
 *
 * Driven by the service's own classification — `wonResolutionTypes` and
 * `systemOnlyResolutionTypes` come from `GET /leads/config`. A local list of
 * "good" outcomes would be a second opinion about the funnel that drifts the
 * moment a resolution is added, and the won set genuinely differs per vertical.
 *
 *  - won        -> green. quote accepted, unit onboarded, booked.
 *  - system-set -> neutral. `unreachable` and `stale_auto_closed` are stamped
 *                  by the service when a quota or a timer runs out. Nobody
 *                  judged the lead, so nothing should look judged.
 *  - everything else -> red. not interested, not qualified, no availability.
 */
export function resolutionStyle(
  resolutionType?: string,
  wonTypes: string[] = [],
  systemTypes: string[] = []
): string {
  if (!resolutionType) return 'bg-neutral-50 text-neutral-400';
  if (wonTypes.includes(resolutionType)) return 'bg-primary-100 text-primary-800';
  if (systemTypes.includes(resolutionType)) return 'bg-neutral-50 text-neutral-400';
  return 'bg-secondary-100 text-secondary-800';
}
